/**
 * E2E renderer probe — serializes the same empty-preset device twice and
 * reports how state-sync's per-key map merge would combine the default groups.
 *
 * Empty presets mint a default group; that key must be stable across
 * serializations of the same device or a later merge creates a second group.
 */
import type { Device } from '#~/device/index.js';
import { jsToPbStudioDeviceControl } from '#~/protobuf/control.js';

interface PresetGroupIdempotencyResult {
  firstGroupKeys: string[];
  secondGroupKeys: string[];
  mergedGroupCount: number;
}

interface PresetGroupIdempotencyProbe {
  serializeSameDeviceTwice(): PresetGroupIdempotencyResult;
}

// Renderer-process gate: read through the preload bridge
// (window.electron.process.env), NOT bare `process.env` — the bundler's
// define replaces `process.env` object reads with `{}` at build time.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const probe: PresetGroupIdempotencyProbe = {
    serializeSameDeviceTwice(): PresetGroupIdempotencyResult {
      // Same identity + empty presets: the serializer must mint one default group.
      const device = {
        id: 'e2e-preset-group-device',
        modelId: 'e2e-preset-group-model',
        control: {
          config: {} as NonNullable<Device['control']>['config'],
          presets: { groups: {}, items: {} }
        }
      } as Device;

      const first = jsToPbStudioDeviceControl(device);
      const second = jsToPbStudioDeviceControl(device);
      const firstGroups = first?.presets?.groups ?? {};
      const secondGroups = second?.presets?.groups ?? {};

      // State-sync merges map fields by key; object-spread is that merge.
      const merged = { ...firstGroups, ...secondGroups };

      return {
        firstGroupKeys: Object.keys(firstGroups),
        secondGroupKeys: Object.keys(secondGroups),
        mergedGroupCount: Object.keys(merged).length
      };
    }
  };

  (
    globalThis as { __irisPresetGroupIdempotencyProbe?: PresetGroupIdempotencyProbe }
  ).__irisPresetGroupIdempotencyProbe = probe;
}
